import React, { useState } from 'react';
import { Grid, TextField, makeStyles } from '@material-ui/core';
import { PostModel } from '../NewsfeedModels';
import UserPostPicture from '../UserPostPicture';
import { PrimaryButton } from 'library';
import { track } from 'shared/Tracking';
import client from 'shared/ApiClient';
import { consoleLogInDev } from 'shared/Utils';
import { useUserContext } from 'UserContext';
import { JobModel } from '../NewsfeedModels';
import CommentActionBar from '../ActionBar/CommentActionBar';
import PendingAttachmentSection from '../Attachments/PendingAttachementSection';
import { ImageListType } from 'react-images-uploading';
import { getRandomCommentPrompt } from '../Prompts';
import { addImages } from '../Utilities/addImageRequest';
import IProvider from 'models/Providers/IProvider';
import theme from 'theme';
interface IBody {
    [key: string]: any;
}

const useStyles = makeStyles({
    root: {
        flexGrow: 1,
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
        padding: 0,
        margin: 0,
        '& .MuiOutlinedInput-root': {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            borderRadius: '15px',
            flex: 1,
            flexDirection: 'column',
        },
    },
});

const AddComment = ({ post, refresh }: { post: PostModel; refresh: () => void }) => {
    const classes = useStyles();
    const [comment, setComment] = useState('');
    const [selectedJob, setSelectedJob] = useState<JobModel>();
    const [selectedProvider, setSelectedProvider] = useState<IProvider>();
    const [images, setImages] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { user } = useUserContext();

    const prompt = getRandomCommentPrompt(user?.id === post.user.id, post.user.role);

    const addComment = async () => {
        setIsSubmitting(true);
        if (comment?.trim().length > 0 || selectedJob || selectedProvider) {
            let form: IBody = {
                postId: post.id,
                comment: comment.trim(),
                attachments: [],
            };

            if (images.length > 0) {
                const imgs = await addImages(images, user);
                imgs.forEach((img) => {
                    form.attachments.push({
                        type: 'Image',
                        data: { imageId: img.id },
                    });
                });
            }

            if (selectedJob) {
                form.attachments = [
                    {
                        type: 'OngoingRequest',
                        data: { jobId: selectedJob.ongoing_id },
                    },
                ];
            }

            if (selectedProvider) {
                form.attachments = [
                    {
                        type: 'Provider',
                        data: { providerId: selectedProvider.id },
                    },
                ];
            }

            track('Add comment', { post, comment });
            client('api/comment/', { body: form })
                .then(() => {
                    setComment('');
                    setSelectedJob(undefined);
                    setImages([]);
                    refresh();
                    setSelectedProvider(undefined);
                })
                .catch(consoleLogInDev)
                .finally(() => {
                    setIsSubmitting(false);
                });
        }
    };

    function handleSetImages(images: ImageListType) {
        setSelectedJob(undefined);
        setSelectedProvider(undefined);
        setImages(images as never[]);
    }

    function handleSetJob(job: JobModel) {
        setImages([]);
        setSelectedProvider(undefined);
        setSelectedJob(job);
    }

    function handleSetProvider(provider: IProvider) {
        setImages([]);
        setSelectedJob(undefined);
        setSelectedProvider(provider);
    }

    return (
        <Grid container justify="flex-end" lg={12}>
            <Grid container justify="flex-end" style={{ paddingTop: 20, paddingBottom: 30 }}>
                <Grid container alignItems="center" style={{ flexWrap: 'nowrap', paddingLeft: 10, paddingRight: 10 }}>
                    <Grid item style={{ alignSelf: 'flex-start' }}>
                        <UserPostPicture profileImage={user?.profile_picture?.image || ''} />
                    </Grid>
                    <Grid item style={{ flexGrow: 1 }}>
                        <Grid container>
                            <TextField
                                classes={classes}
                                variant="outlined"
                                placeholder={prompt}
                                value={comment}
                                onChange={(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
                                    setComment(event.target.value)
                                }
                                multiline
                                InputProps={{
                                    endAdornment: (
                                        <Grid container>
                                            <PendingAttachmentSection
                                                images={images}
                                                setImages={setImages}
                                                job={selectedJob}
                                                setJob={setSelectedJob}
                                                setProvider={setSelectedProvider}
                                                provider={selectedProvider}
                                            />
                                        </Grid>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid
                            xs={12}
                            container
                            direction="row"
                            style={{ alignItems: 'center', justifyContent: 'space-between', paddingTop: 5 }}
                        >
                            <Grid xs={5} item>
                                <CommentActionBar
                                    images={images}
                                    setImages={handleSetImages}
                                    setJob={handleSetJob}
                                    setProvider={handleSetProvider}
                                />
                            </Grid>
                            <Grid xs={5} item style={{ marginLeft: 5 }}>
                                <PrimaryButton onClick={() => !isSubmitting && addComment()} loading={isSubmitting}>
                                    Comment
                                </PrimaryButton>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default AddComment;
